<template>
    <div class="w-full h-[90%]">
        <form @submit.prevent="submitForm" action="" v-if="user" class="flex flex-col h-full w-full justify-between">
            <div class="grid grid-cols-3 gap-2">
                <div class="flex items-center">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        First Name:
                    </label>
                    <input type="text" class="p-1 border h-full w-full" v-model="userData.first_name">
                </div>
                <div class="flex items-center">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        Last Name:
                    </label>
                    <input type="text" class="p-1 border h-full w-full" v-model="userData.last_name">
                </div>
                <div class="flex items-center">
                    <label class="whitespace-nowrap tracking-wide font-semibold pr-1 text-gray-800">
                        Is Active:
                    </label>
                    <select class="p-1 border h-full w-full" v-model="userData.is_active">
                        <option :value="true">True</option>
                        <option :value="false">False</option>
                    </select>
                </div>
            </div>
            <div class="grid grid-cols-2 gap-1 w-full my-2">
              <div class="col-span-2 w-full border-b tracking-wide font-semibold pr-1 text-gray-800">
                User Groups
              </div>
              <div class="flex w-full justify-center">
                <div class="flex flex-col border w-5/6">
                  <div class="w-full bg-gray-200 p-2 flex items-center justify-center">
                    Available Groups
                  </div>
                  <div class="flex flex-col p-2 h-44 overflow-auto">
                    <div v-for="group in availableUserGroups" :key="group.id" :draggable="true" :ondragstart="dragStartHandler" :ondragend="dragEndHandler" :id="group.id" class="m-1 p-1 border rounded-md hover:cursor-grab">
                        {{ group.name }}
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex w-full justify-center">
                <div class="flex flex-col border w-5/6">
                  <div class="w-full bg-gray-200 p-2 flex items-center justify-center">
                    Assigned Groups
                  </div>
                  <div class="flex flex-col p-2 h-44 overflow-auto w-full" :ondragover="dragOverHandler" :ondrop="dropHandler">
                    <div v-for="group in userData.groups" :key="group.id" class="m-1 p-1 border rounded-md flex items-center justify-between">
                      <div>
                        {{ group.name }}
                      </div>
                      <div>
                        <base-button-negative @click.prevent="removeGroup(group.id)">
                          <XMarkSVG strokeColor="white" />
                        </base-button-negative>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-span-2 w-full mt-1">
                <base-accordion :start-collapsed="true">
                  <template #title>
                    <div>Current Users and Groups</div>
                  </template>
                  <template #accordian-body>
                    <div class="grid grid-cols-4 gap-1">
                      <div class="border flex flex-col" v-for="activeUser in usersWithGroups" :key="activeUser.id">
                        <div class="w-full bg-gray-200 p-1 flex items-center justify-center">
                            {{ activeUser.first_name }} {{ activeUser.last_name }}
                        </div>
                        <div class="flex flex-col p-2 h-44 overflow-auto w-full">
                          <div v-for="group in activeUser.groups" :key="group.id" class="m-1 p-1 border-b flex items-center justify-between">
                            <div>
                              {{ group.name }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </base-accordion>
              </div>
            </div>
            <div class="w-full flex items-center justify-center">
                <div class="mx-1">
                    <base-button-affirmative>
                        Save
                    </base-button-affirmative>
                </div>
                <div class="mx-1">
                    <base-button-negative @click.prevent="close">
                        Cancel
                    </base-button-negative>
                </div>
            </div>
        </form>
        <div v-else class="text-lg">
            No Aerocle user associated with this person
        </div>
    </div>
</template>

<script setup>
import { defineProps, reactive, computed, defineEmits, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import { updateFullUser, getAllUsersWithGroups } from '@/services/User';
import XMarkSVG from '../svg-components/XMarkSVG.vue';

const store = useStore()
const $toast = useToast()

const props = defineProps(['user', 'groups', 'allUsers'])
const emit = defineEmits(['close', 'refetch-users'])

const allUsers = ref([])

const userData = reactive({
    first_name: props.user ? props.user.first_name : null,
    last_name: props.user ? props.user.last_name : null,
    is_active: props.user ? props.user.is_active : null,
    groups: props.user ? props.user.groups : null
})

function close(){
    emit('close')
}

async function submitForm(){
    const token = store.state.auth.token
    const result = await updateFullUser(token, props.user.id, userData)
    if(result.status == 200){
        $toast.open({
            message: `Successfully updated ${props.user.username}`,
            type: 'success',
            duration: 3000
        })
        emit('refetch-users')
        emit('close')
    } else {
        $toast.open({
            message: `Error, could not update ${props.user.username}`,
            type: 'error',
            duration: 3000
        })
    }
}


const usersWithGroups = computed(() => {
  return allUsers.value.filter((user) => user.groups.length > 0)
})

const isDragging = ref(false)
const draggingGroup = ref(null)

function dragOverHandler(ev) {
  ev.preventDefault();
}

function dropHandler(ev) {
  ev.preventDefault();
  isDragging.value = false
  userData.groups.push(draggingGroup.value)
}

function dragStartHandler(ev) {
  isDragging.value = true
  ev.dataTransfer.effectAllowed = "copyMove";
  draggingGroup.value = props.groups.find((group) => {
    return group.id == ev.target.id
  })
}

function dragEndHandler(ev) {
    ev.preventDefault()
    isDragging.value = false
    draggingGroup.value = null
}

const availableUserGroups = computed(() => {
  const userGroupIds = userData.groups.map((group) => group.id)
  return props.groups.filter((group) => {
    return !userGroupIds.includes(group.id)
  })
})

function removeGroup(groupId){
  userData.groups.splice(userData.groups.findIndex((group) => group.id == groupId), 1)
}

async function loadAllUsers(){
  const token = store.state.auth.token
  const result = await getAllUsersWithGroups(token)
  if(result.status == 200){
    allUsers.value = result.data
  }
}

loadAllUsers()

</script>